import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { GalleryCard } from "@/components/GalleryCard";
import { EVENTS_LIST } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAG_COLORS = {
  Annual: "bg-ember text-sand",
  Competition: "bg-teal text-sand",
  Excursion: "bg-teal-subtle text-teal-deep",
  Fun: "bg-ember-light text-coal",
  Camp: "bg-gold text-coal",
  Educators: "bg-coal text-sand",
};

export default function Events() {
  const [items, setItems] = useState(null);
  const [year, setYear] = useState("All");

  const YEARS = ["All", "2026", "2025", "2024", "2023", "2022"];
  const visible =
    items === null ? null : year === "All" ? items : items.filter((i) => i.year === year);

  useEffect(() => {
    axios
      .get(`${API}/gallery`, { params: { club: "events" } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="events-page">
      <section className="relative overflow-hidden bg-ember-subtle grain py-16">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-teal-subtle blur-[90px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <Eyebrow>Programme events</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl lg:text-6xl">
              Events & <span className="text-ember">Gallery</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-coal-muted md:text-lg">
              Theatre nights, museum mornings, island camps and award ceremonies, the moments that
              turn programmes into memories.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>On the calendar</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Signature events
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {EVENTS_LIST.map((e, i) => (
              <Reveal key={e.title} delay={(i % 3) * 0.08}>
                <div
                  className={`flex h-full flex-col rounded-[2rem] bg-white p-7 shadow-[0_12px_40px_-26px_rgba(26,29,36,0.4)] transition-all duration-500 hover:-translate-y-1.5 hover:rotate-0 ${
                    i % 2 ? "-rotate-[0.6deg]" : "rotate-[0.6deg]"
                  }`}
                  data-testid={`event-card-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`rounded-full px-3 py-1 font-mono-brand text-[10px] font-bold uppercase tracking-[0.2em] ${TAG_COLORS[e.tag]}`}>
                      {e.tag}
                    </span>
                    <CalendarDays size={18} className="text-coal/20" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-bold leading-snug text-coal">{e.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-coal-muted">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-teal-subtle py-16" data-testid="events-gallery">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>From the archive</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Moments worth sharing
            </h2>
            <p className="mt-3 max-w-xl text-sm text-coal-muted">
              Like, comment and share, tag us on Instagram, Facebook or TikTok and spread the word.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="gallery-year-filters">
              {YEARS.map((y) => (
                <button
                  key={y}
                  data-testid={`filter-year-${y.toLowerCase()}`}
                  onClick={() => setYear(y)}
                  className={`rounded-full px-5 py-2 font-heading text-sm font-bold transition-all duration-300 ${
                    year === y
                      ? "bg-coal text-sand shadow-md"
                      : "bg-white text-coal-muted hover:bg-ember hover:text-sand"
                  }`}
                >
                  {y}
                </button>
              ))}
              {visible && (
                <span className="ml-2 font-mono-brand text-xs font-semibold tracking-widest text-coal-muted" data-testid="gallery-count">
                  {visible.length} {visible.length === 1 ? "moment" : "moments"}
                </span>
              )}
            </div>
          </Reveal>
          {visible === null ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="events-gallery-loading">Loading gallery...</p>
          ) : visible.length === 0 ? (
            <p className="mt-10 text-sm text-coal-muted" data-testid="events-gallery-empty">No moments from {year} yet.</p>
          ) : (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, i) => (
                <GalleryCard key={item.id} item={item} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
